// frontend/src/components/tables/TopVolumeTablesByInterval.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function TopVolumeTablesByInterval({
  visiblePlotLines,
  onToggleLine,
}) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = () => {
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/tables/top_volume_by_interval`
      )
      .then((response) => {
        setData(response.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || '데이터를 불러오는 중 오류가 발생했습니다.');
        setLoading(false);
      });
  };

  // 10초마다 데이터 갱신
  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 10_000);
    return () => clearInterval(intervalId);
  }, []);

  // RESET 버튼 핸들러: 버튼 클릭 시, 모든 구간의 데이터에 대해 각 row의 composite key를 "구간_순위"로 생성해서
  // rank가 1~5이면 true, 6 이상이면 false로 초기 상태로 되돌립니다.
  const handleReset = () => {
    // 데이터가 아직 로드되지 않았다면 아무 작업도 하지 않음
    if (!data || Object.keys(data).length === 0) return;

    Object.keys(data).forEach((interval) => {
      data[interval].forEach((row) => {
        const key = `${interval}_${row.rank}`;
        onToggleLine(key, row.rank <= 5, {
          interval,
          rank: row.rank,
          timestamp_korean: row.timestamp_korean,
          close: row.close,
          volume: row.volume,
          power: row.power,
        });
      });
    });
  };

  const handleUncheckAll = () => {
    Object.keys(data).forEach((interval) => {
      data[interval].forEach((row) => {
        const key = `${interval}_${row.rank}`;
        onToggleLine(key, false, {
          interval,
          rank: row.rank,
          timestamp_korean: row.timestamp_korean,
          close: row.close,
          volume: row.volume,
          power: row.power,
        });
      });
    });
  };

  // ISO 문자열을 KST 로컬 시간 "YYYY-MM-DD HH:mm:ss" 형식으로 보여주는 헬퍼
  const formatKST = (isoString) => {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('ko-KR', {
      timeZone: 'Asia/Seoul',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .format(date)
      .replace(/\./g, '-')
      .replace(/\s/, ' ');
  };

  if (loading) return <div style={{ textAlign: 'center' }}>Loading...</div>;
  if (error)
    return (
      <div style={{ textAlign: 'center', color: 'red' }}>Error: {error}</div>
    );

  // 공통 클래스
  const tableClass = 'w-full border-collapse';
  const theadThClass =
    'border border-neutral-300 bg-neutral-100 px-3 py-2 text-center text-m font-medium whitespace-nowrap';
  const tdClass =
    'border border-neutral-300 bg-white px-3 py-2 text-center text-m whitespace-nowrap';

  // 정렬된 구간 순서를 지정합니다.
  const intervals = ['1h', '2h', '3h', '4h', '6h', '12h', '24h'];
  const tableLabel = {
    '1h': '1시간',
    '2h': '2시간',
    '3h': '3시간',
    '4h': '4시간',
    '6h': '6시간',
    '12h': '12시간',
    '24h': '24시간',
  };

  // 시간대별 색상 매핑
  const colorMapping = {
    1: '#FF0000', // 빨간색
    2: '#FFA500', // 주황색
    3: '#ffd907', // 노란색
    4: '#008000', // 녹색
    5: '#800080', // 보라색
    '6-10': '#a5a5a5', // 회색
  };

  return (
    <div className="grid gap-2 my-5 mx-auto max-w-9xl font-sans grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      <div>
        {/* Max/Min 정보 테이블 */}
        <div className="my-5 mx-auto max-w-[600px]">
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={theadThClass}>구분</th>
                <th className={theadThClass}>가격</th>
                <th className={theadThClass}>존(순위)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={tdClass}>MAX</td>
                <td className={tdClass}>16,250.25</td>
                <td className={tdClass}>2(1)</td>
              </tr>
              <tr>
                <td className={tdClass}>MIN</td>
                <td className={tdClass}>16,100.50</td>
                <td className={tdClass}>4(2)</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* colorMapping 표 */}
        <div className="my-5 mx-auto max-w-[600px] border border-neutral-300 rounded-md overflow-hidden shadow-sm">
          <div className="bg-neutral-100 px-3 py-2 text-center font-bold">
            <span>비고</span>
          </div>
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={theadThClass}>순위</th>
                <th className={theadThClass}>라인 색상</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(colorMapping).map(([rank, color]) => (
                <tr key={rank}>
                  <td className={tdClass}>{rank}</td>
                  <td className={tdClass}>
                    <div
                      style={{ backgroundColor: color }}
                      className="w-10 h-5 border border-black mx-auto"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {intervals.map((interval) => (
        <div
          key={interval}
          className="border border-neutral-300 rounded-md overflow-hidden shadow-sm"
        >
          <div className="bg-neutral-100 px-3 py-2 text-center font-bold">
            <span>{tableLabel[interval]} 구간</span>
          </div>
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={theadThClass}>순위</th>
                <th className={theadThClass}>발생시각</th>
                <th className={theadThClass}>종가</th>
                <th className={theadThClass}>체결량</th>
                <th className={theadThClass}>강도</th>
                <th className={theadThClass}>표시</th>
              </tr>
            </thead>
            <tbody>
              {data[interval] && data[interval].length > 0 ? (
                data[interval].map((row, idx) => {
                  const toggleKey = `${interval}_${row.rank}`;
                  const v = visiblePlotLines[toggleKey];
                  const checked =
                    typeof v === 'object' && v !== null ? !!v.checked : !!v;

                  return (
                    <tr key={idx}>
                      <td className={tdClass}>{row.rank}</td>
                      <td className={tdClass}>
                        {formatKST(row.timestamp_korean)}
                      </td>
                      <td className={tdClass}>{row.close}</td>
                      <td className={tdClass}>{row.volume}</td>
                      <td className={tdClass}>{row.power}</td>
                      <td className={tdClass}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            onToggleLine(toggleKey, e.target.checked, {
                              interval,
                              rank: row.rank,
                              timestamp_korean: row.timestamp_korean,
                              close: row.close,
                              volume: row.volume,
                              power: row.power,
                            })
                          }
                          className="accent-sky-600"
                        />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className={tdClass} colSpan="6">
                    No data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ))}

      <div className="text-center mt-5">
        <button
          onClick={handleReset}
          className="inline-flex items-center justify-center rounded-md bg-sky-600 px-4 py-2 text-white text-sm font-medium hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 mr-8"
        >
          1~5위 선택
        </button>
        <button
          onClick={handleUncheckAll}
          className="inline-flex items-center justify-center rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
        >
          모든 체크 해제
        </button>
      </div>
    </div>
  );
}
